import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { runtime } from '~/lib/runtime';

/**
 * Recoverable state for a sandbox that failed to start (DEV-146). A boot failure
 * used to leave the runtime stuck at 'booting'/'error' with NO UI — the user was
 * stranded on a silently-broken edit page while background polling hammered a dead
 * pod. This renders on status==='error' and offers an explicit Retry that re-boots.
 */
export function SandboxErrorBanner() {
  const status = useStore(runtime.status);
  const [retrying, setRetrying] = useState(false);

  if (status !== 'error' && !retrying) {
    return null;
  }

  const handleRetry = async () => {
    const chatId = runtime.getChatId();

    if (!chatId || retrying) {
      return;
    }

    setRetrying(true);

    try {
      await runtime.boot(chatId);
    } catch (err) {
      // boot() already set status back to 'error'; keep the banner up so the user
      // can try again. Surfaced here (not swallowed) so it reaches the console.
      console.error('Sandbox retry failed:', err);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="ui-banner-idle w-full">
      <span className="i-ph:warning-circle text-danger text-[14px] flex-shrink-0" />
      <span className="flex-1 min-w-0">Your workspace failed to start. Your project files are saved.</span>
      <button
        type="button"
        onClick={handleRetry}
        disabled={retrying}
        className="ui-btn-ghost flex items-center gap-1 disabled:cursor-wait"
      >
        {retrying ? (
          <>
            <span className="i-svg-spinners:90-ring-with-bg text-[12px]" />
            Retrying…
          </>
        ) : (
          <>
            <span className="i-ph:arrow-clockwise text-[12px]" />
            Retry
          </>
        )}
      </button>
    </div>
  );
}
